import { useId, useRef, useState, type SubmitEvent } from "react";
import { useTranslation } from "react-i18next";

import type { DegreeOfSeparation } from "@/api/bale.zod";

import { Button } from "./Button";
import { Dropdown } from "./Dropdown";
import { Form } from "./Form";
import { Icon } from "./Icon";
import { Input } from "./Input";
import { MenuField } from "./MenuField";
import { MenuItem } from "./MenuItem";

export function DegreeOfSeparationInput({
  defaultValue,
  onChange,
  disabled = false,
  max = null,
  naming = "visibility",
}: {
  naming?: "visibility" | "invitability";
  defaultValue: DegreeOfSeparation;
  onChange?: (value: DegreeOfSeparation) => void;
  disabled?: boolean;
  max?: DegreeOfSeparation | null;
}) {
  const { t } = useTranslation();
  const id = useId();
  const popover = useRef<HTMLDivElement>(null);
  const [value, setValue] = useState<DegreeOfSeparation>(defaultValue);
  const [isEditingDistance, setIsEditingDistance] = useState(false);

  function change(value: DegreeOfSeparation) {
    setIsEditingDistance(false);
    popover.current?.hidePopover();
    setValue(value);
    onChange?.(value);
  }

  function saveDistance(event: SubmitEvent<HTMLFormElement>) {
    const form = new FormData(event.currentTarget);
    const discoverabilityDistance = Number.parseInt(form.get("discoverability_distance")! as string, 10);
    change(discoverabilityDistance);
  }

  return (
    <>
      <Button
        type="button"
        disabled={disabled}
        popoverTarget={id}
        className="flex flex-row items-center justify-between"
      >
        {naming === "visibility"
          ? t("degrees-of-separation-visibility-value", {
              value: typeof max === "number" && typeof value === "number" ? Math.min(value, max) : value,
            })
          : t("degrees-of-separation-invitability-value", {
              value: typeof max === "number" && typeof value === "number" ? Math.min(value, max) : value,
            })}
        <Icon name="chevron-down" />
      </Button>
      <Dropdown
        id={id}
        popover="auto"
        className="absolute -mt-2 mr-2 min-w-[anchor-size(width)] [position-area:bottom_span-right]"
        ref={popover}
        onToggle={() => setIsEditingDistance(false)}
      >
        <MenuItem disabled={typeof max === "number"} onClick={() => change("unlimited")}>
          {naming === "visibility"
            ? t("degrees-of-separation-visibility-value", { value: "unlimited" })
            : t("degrees-of-separation-invitability-value", { value: "unlimited" })}
        </MenuItem>
        <MenuItem onClick={() => change(0)}>
          {" "}
          {naming === "visibility"
            ? t("degrees-of-separation-visibility-value", { value: 0 })
            : t("degrees-of-separation-invitability-value", { value: 0 })}
        </MenuItem>
        <MenuItem disabled={typeof max === "number" && max < 1} onClick={() => change(1)}>
          {naming === "visibility"
            ? t("degrees-of-separation-visibility-value", { value: 1 })
            : t("degrees-of-separation-invitability-value", { value: 1 })}
        </MenuItem>
        <MenuItem disabled={typeof max === "number" && max < 2} onClick={() => change(2)}>
          {naming === "visibility"
            ? t("degrees-of-separation-visibility-value", { value: 2 })
            : t("degrees-of-separation-invitability-value", { value: 2 })}
        </MenuItem>
        {isEditingDistance ? (
          <MenuField>
            <Form onSubmit={saveDistance} className="flex w-full flex-row items-center gap-4">
              <div>{t("degree-of-separation")}</div>
              <div className="grow">
                <Input
                  type="number"
                  required
                  name="discoverability_distance"
                  autoFocus
                  placeholder={value === "unlimited" ? "3" : value.toString()}
                />
              </div>
            </Form>
          </MenuField>
        ) : (
          <MenuItem onClick={() => setIsEditingDistance(true)}>{t("custom-discoverability")}</MenuItem>
        )}
      </Dropdown>
    </>
  );
}
